/**
 * UUID Generator Tool
 * 
 * Generates UUIDs in various formats.
 */

import { v4 as uuidv4, v7 as uuidv7 } from 'uuid';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { IGeneratorTool } from '@/interfaces/ITool';

export const UuidGeneratorTool: IGeneratorTool = {
  id: 'uuid-generator',
  name: 'UUID Generator',
  description: 'Generate UUIDs (v4/v7)',
  version: '1.0.0',
  type: 'generator',
  category: 'Generators',
  keywords: ['uuid', 'guid', 'generate', 'unique', 'id', 'generator'],

  seo: {
    slug: 'uuid-generator',
    pageTitle: 'UUID Generator - Free Online v4/v7 Tool',
    metaDescription: 'Generate random UUIDs (v4) and time-ordered UUIDs (v7) instantly. Bulk generation supported. Fast, private, runs entirely in your browser.',
    keywords: ['uuid generator', 'guid generator', 'uuid v4', 'uuid v7', 'random uuid online'],
    relatedTools: ['nanoid-generator'],
    schemaType: 'WebApplication',
  },

  outputType: SEMANTIC_TYPES.UUID,

  attributes: [
    {
      name: 'count',
      type: 'number',
      label: 'Count',
      defaultValue: 1,
      validation: { min: 1, max: 100 },
      description: 'Number of UUIDs to generate',
    },
    {
      name: 'version',
      type: 'select',
      label: 'Version',
      defaultValue: 'v4',
      options: ['v4', 'v7'],
      description: 'UUID version to generate',
    },
    {
      name: 'format',
      type: 'select',
      label: 'Format',
      defaultValue: 'standard',
      options: ['standard', 'uppercase', 'no-dashes', 'braces'],
      description: 'Output format for UUIDs',
    },
  ],

  async generate(config: Record<string, unknown>): Promise<string> {
    const count = (config.count as number) || 1;
    const version = String(config.version ?? 'v4');
    const format = (config.format as string) || 'standard';

    const uuids: string[] = [];

    for (let i = 0; i < count; i++) {
      let uuid = version === 'v7' ? uuidv7() : uuidv4();

      switch (format) {
        case 'uppercase':
          uuid = uuid.toUpperCase();
          break;
        case 'no-dashes':
          uuid = uuid.replace(/-/g, '');
          break;
        case 'braces':
          uuid = `{${uuid}}`;
          break;
      }

      uuids.push(uuid);
    }

    return uuids.join('\n');
  },
};

